import { CommonModule } from '@angular/common';
import {
    Component,
    OnInit,
    TemplateRef,
    ViewChild
} from '@angular/core';
import { Tooltip } from 'primeng/tooltip';
import { map } from 'rxjs/operators';

import { StatisticsInProgressComponent } from '../in-progress/in-progress.component';
import { StatisticsSummaryChartsComponent } from '../summary/summary-charts.component';

import { AuthService } from '@/auth/services';
import { DataTableComponent } from '@/components';
import {
    DataTableColumnType,
    FilterType
} from '@/enums';
import {
    DataTableColumn,
    DataTableExportParams,
    DistrictProcessingStatistic,
    DistrictProcessingStatisticSummary
} from '@/interfaces';
import { DistrictProcessingStatisticsService } from '@/service';

interface RateSegment {
    key: string;
    label: string;
    value: number;
    percent: number;
    labelClass: string;
    barClass: string;
    tooltip: string;
}

@Component({
    selector: 'app-statistics-listing',
    standalone: true,
    templateUrl: 'listing.component.html',
    imports: [
        CommonModule,
        DataTableComponent,
        Tooltip,
        StatisticsSummaryChartsComponent,
        StatisticsInProgressComponent
    ]
})
export class StatisticsListingComponent implements OnInit {
    summary: DistrictProcessingStatisticSummary | null = null;
    summaryLoading = false;

    readonly readPermission = 'districts.district_processing_statistics.read';
    readonly exportPermission = 'districts.district_processing_statistics.export';

    private readonly exportColumns = [
        { field: 'state', header: 'State' },
        { field: 'year', header: 'Year' },
        { field: 'total_districts', header: 'Total districts' },
        { field: 'districts_with_agenda_url', header: 'With agenda URL' },
        { field: 'districts_crawled', header: 'Districts crawled' },
        { field: 'districts_analyzed', header: 'Districts analyzed' },
        { field: 'districts_without_meetings', header: 'Districts w/o meetings' },
        { field: 'files_analyzed', header: 'Files analyzed' },
        { field: 'distinct_rates', header: 'Rates - Total' },
        { field: 'hourly_rates', header: 'Rates - Hourly' },
        { field: 'daily_rates', header: 'Rates - Daily' },
        { field: 'other_rates', header: 'Rates - Other' },
        { field: 'service_providers', header: 'Service providers' }
    ];

    @ViewChild('countCell', { static: true }) countCell!: TemplateRef<unknown>;
    @ViewChild('ratesCell', { static: true }) ratesCell!: TemplateRef<unknown>;

    columns: DataTableColumn<DistrictProcessingStatistic>[] = [];

    private readonly baseColumns: DataTableColumn<DistrictProcessingStatistic>[] = [
        {
            field: 'state',
            header: 'State',
            filterType: FilterType.MULTISELECT,
            filterOptionsFn: (context) => this.options(context).pipe(
                map((o) => o.states.map((v) => ({ label: v, value: v })))
            ),
            frozen: true,
            minWidth: 100
        },
        {
            field: 'year',
            header: 'Year',
            filterType: FilterType.MULTISELECT,
            filterOptionsFn: (context) => this.options(context).pipe(
                map((o) => o.years.map((v) => ({ label: String(v), value: v })))
            ),
            minWidth: 90
        },
        {
            field: 'total_districts',
            header: 'Total districts',
            type: DataTableColumnType.NUMBER,
            filterable: false,
            minWidth: 120
        },
        {
            field: 'districts_with_agenda_url',
            header: 'With agenda URL',
            type: DataTableColumnType.NUMBER,
            filterable: false,
            minWidth: 130
        },
        {
            field: 'districts_crawled',
            header: 'Districts crawled',
            filterable: false,
            sortable: true,
            minWidth: 130
        },
        {
            field: 'districts_analyzed',
            header: 'Districts analyzed',
            filterable: false,
            sortable: true,
            minWidth: 140
        },
        {
            field: 'districts_without_meetings',
            header: 'Districts w/o meetings',
            type: DataTableColumnType.NUMBER,
            filterable: false,
            minWidth: 150
        },
        {
            field: 'files_analyzed',
            header: 'Files analyzed',
            type: DataTableColumnType.NUMBER,
            filterable: false,
            minWidth: 120
        },
        {
            field: 'distinct_rates',
            header: 'Rates',
            filterable: false,
            sortable: true,
            minWidth: 280
        },
        {
            field: 'service_providers',
            header: 'Service providers',
            type: DataTableColumnType.NUMBER,
            filterable: false,
            minWidth: 130
        }
    ];

    constructor(
        public authService: AuthService,
        private service: DistrictProcessingStatisticsService
    ) {}

    ngOnInit(): void {
        this.refreshSummary({});

        this.columns = this.baseColumns.map((col) => {
            if (col.field === 'districts_crawled' || col.field === 'districts_analyzed') {
                return { ...col, template: this.countCell };
            }
            if (col.field === 'distinct_rates') {
                return { ...col, template: this.ratesCell };
            }
            return col;
        });
    }

    loadData = (params: Record<string, any>) => {
        this.refreshSummary(params);

        return this.service.listing(params);
    };

    exportData = (params: DataTableExportParams) => this.service.export({ ...params, columns: this.exportColumns });

    options = (context: Record<string, any> = {}) => this.service.options(context);

    canView = () => this.authService.hasPermission(this.readPermission);

    private refreshSummary(params: Record<string, any>): void {
        this.summaryLoading = true;
        this.service.summary(this.summaryParams(params)).subscribe({
            next: (summary) => {
                this.summary = summary;
                this.summaryLoading = false;
            },
            error: () => {
                this.summary = null;
                this.summaryLoading = false;
            }
        });
    }

    private summaryParams(params: Record<string, any>): Record<string, any> {
        const out: Record<string, any> = {};

        for (const [key, value] of Object.entries(params)) {
            if (key.startsWith('filter[')) {
                out[key] = value;
            }
        }

        return out;
    }

    rateSegments(row: DistrictProcessingStatistic): RateSegment[] {
        const total = row.distinct_rates ?? 0;
        const pct = (value: number) => this.segmentPercent(value, total);

        return [
            {
                key: 'hourly',
                label: 'Hourly',
                value: row.hourly_rates ?? 0,
                percent: pct(row.hourly_rates ?? 0),
                labelClass: 'text-green-600',
                barClass: 'bg-green-500',
                tooltip: this.segmentTooltip('Hourly', row.hourly_rates ?? 0, total)
            },
            {
                key: 'daily',
                label: 'Daily',
                value: row.daily_rates ?? 0,
                percent: pct(row.daily_rates ?? 0),
                labelClass: 'text-orange-600',
                barClass: 'bg-orange-500',
                tooltip: this.segmentTooltip('Daily', row.daily_rates ?? 0, total)
            },
            {
                key: 'other',
                label: 'Other',
                value: row.other_rates ?? 0,
                percent: pct(row.other_rates ?? 0),
                labelClass: 'text-sky-600',
                barClass: 'bg-sky-400',
                tooltip: this.segmentTooltip('Other', row.other_rates ?? 0, total)
            }
        ];
    }

    ratesStackTooltip(row: DistrictProcessingStatistic): string {
        const total = row.distinct_rates ?? 0;
        if (total === 0) {
            return 'No rates loaded';
        }

        const hourly = row.hourly_rates ?? 0;
        const daily = row.daily_rates ?? 0;
        const other = row.other_rates ?? 0;
        const share = (n: number) => ((n / total) * 100).toFixed(1);

        return [`Total: ${total.toLocaleString()}`, `Hourly: ${hourly.toLocaleString()} (${share(hourly)}%)`, `Daily: ${daily.toLocaleString()} (${share(daily)}%)`, `Other: ${other.toLocaleString()} (${share(other)}%)`].join('\n');
    }

    segmentPercent(value: number, total: number): number {
        if (total <= 0 || value <= 0) {
            return 0;
        }

        return Math.min(100, (value / total) * 100);
    }

    segmentTooltip(label: string, value: number, total: number): string {
        if (total <= 0) {
            return `${label}: ${value.toLocaleString()}`;
        }

        const pct = ((value / total) * 100).toFixed(1);

        return `${label}: ${value.toLocaleString()} (${pct}% of total)`;
    }
}
